import { ExternalLinkIcon } from '@heroicons/react/solid';
import {
  Alert,
  Badge,
  Card,
  Link,
  Pagination,
  Select,
  SkeletonLoader,
  Table,
} from '@yoast/ui-library';
import cx from 'classnames';
import { useEffect, useState } from 'react';

// import type { CitationsResponseResultsItem } from '../../api/generated/insights.schemas';
import createComponent from '../../components/CreateComponent';
import TextScore from '../../components/TextScore';
import TitleWithTooltip from '../../components/TitleWithTooltip';
import useScan from '../../hooks/useScan';
import { getCategoryColor } from '../../utils/categoryColors';
import { getDeltaTextClassNames } from '../../utils/getDeltaTextClassNames';

// TODO: this will be removed and pulled in from insights.schemas later
type CitationsResponseResultsItem = {
  rank?: number;
  url?: string;
  rootDomain?: string;
  category?: string;
  impact?: 'High' | 'Medium' | 'Low';
  mentions?: number;
};

interface CitationTableRowProps {
  data?: CitationsResponseResultsItem;
  totalQueries?: number;
  isLoading?: boolean;
  isFirstScan?: boolean;
  enableCategories?: boolean;
}

const MobileOnly = createComponent`md:yst-hidden yst-mr-2 yst-font-medium yst-text-slate-400 yst-inline-flex yst-w-20`;

const CitationTableRow = ({
  data,
  totalQueries,
  isLoading = false,
  isFirstScan = false,
  enableCategories = false,
}: CitationTableRowProps) => {
  const showSkeleton = isLoading || !data;
  const {
    rank,
    url,
    rootDomain,
    category,
    impact,
    // @ts-expect-error - schema difference
    numberOfMentions,
  } = data || {};

  // Safely extract mentions value, handling undefined cases
  const nrOfMentions = numberOfMentions?.value;
  const previousNrOfMentions = numberOfMentions?.previous;

  // More mentions is better, so positive difference is good.
  const nrOfMentionsDelta =
    typeof previousNrOfMentions?.difference === 'number'
      ? previousNrOfMentions!.difference
      : nrOfMentions;

  return (
    <tr
      key={showSkeleton ? undefined : url}
      className="yst-flex yst-flex-col md:yst-table-row"
    >
      <Table.Cell
        // make table responsive
        className="yst-ml-2 yst-py-1 md:yst-ml-0 md:yst-p-2"
      >
        <MobileOnly as="span">Rank:</MobileOnly>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-24" />
        ) : (
          <span className="yst-font-medium yst-text-slate-900">
            {rank ?? '—'}
          </span>
        )}
      </Table.Cell>
      <Table.Cell
        // make table responsive
        // xs should be in the block view
        // md+ should be table
        className="yst-ml-2 yst-py-1 md:yst-ml-0 md:yst-p-2 yst-break-all"
      >
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-full" />
        ) : (
          <div className="yst-flex yst-items-center yst-gap-2">
            <Link
              className="yst-flex yst-items-center yst-gap-1 yst-font-medium"
              href={url || '#'}
              rel="nofollow noreferrer"
              target="_blank"
            >
              <span>{url ?? '—'}</span> <ExternalLinkIcon />
            </Link>
          </div>
        )}
      </Table.Cell>
      <Table.Cell
        // make table responsive
        className="yst-ml-2 yst-py-1 yst-font-medium md:yst-ml-0 md:yst-p-2"
      >
        <MobileOnly as="span">Domain:</MobileOnly>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-24" />
        ) : (
          <span className="yst-font-medium yst-text-slate-900">
            {rootDomain ?? '—'}
          </span>
        )}
      </Table.Cell>
      {enableCategories && (
        <Table.Cell
          // make table responsive
          className="yst-ml-2 yst-py-1 md:yst-ml-0 md:yst-p-2"
        >
          <MobileOnly as="span">Category:</MobileOnly>
          {showSkeleton ? (
            <SkeletonLoader className="yst-h-6 yst-w-16" />
          ) : (
            <Badge
              className={cx(
                getCategoryColor(category || '').bg,
                getCategoryColor(category || '').text,
              )}
            >
              {category ?? '—'}
            </Badge>
          )}
        </Table.Cell>
      )}
      <Table.Cell
        className={cx(
          //mobile
          'yst-ml-2 yst-py-0 md:yst-ml-0 md:yst-p-2',
          // etc
          'yst-font-medium',
          {
            'yst-text-green-600': impact === 'High',
            'yst-text-yellow-600': impact === 'Medium',
            'yst-text-red-600': impact === 'Low',
          },
        )}
      >
        <MobileOnly as="span">Impact:</MobileOnly>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-12" />
        ) : (
          (impact ?? '—')
        )}
      </Table.Cell>
      <Table.Cell
        // make table responsive
        className="yst-ml-2 yst-pt-1 md:yst-ml-0 md:yst-p-2 md:yst-text-right"
      >
        <MobileOnly as="span">Mentions:</MobileOnly>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-8" />
        ) : nrOfMentions ? (
          <span className="yst-flex yst-justify-end yst-gap-4">
            <span className="yst-flex yst-flex-nowrap yst-gap-1 yst-font-medium">
              <span className="yst-text-slate-500">{nrOfMentions}</span>
              <span className="yst-text-slate-900">/ {totalQueries}</span>
            </span>

            {!isFirstScan && (
              <span
                className={cx(
                  'yst-ml-auto',
                  getDeltaTextClassNames(nrOfMentionsDelta),
                )}
              >
                {nrOfMentionsDelta > -1 && '+'}
                {nrOfMentionsDelta}
              </span>
            )}
          </span>
        ) : (
          '—'
        )}
      </Table.Cell>
    </tr>
  );
};

interface CitationsCardProps {
  enableCategories?: boolean;
}

const CitationsCard = ({ enableCategories = false }: CitationsCardProps) => {
  // const showPagination = false;
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { data: scan, isLoading, isRefetching } = useScan();
  const citations = scan?.citations;

  const standardizedCitationScore = scan?.scores?.standardized?.citations || {};
  const hasPreviousCitation = Object.hasOwn(
    standardizedCitationScore,
    'previous',
  );

  // Filter citations based on selected category
  const filteredResults = citations?.list?.filter((citation) => {
    if (selectedCategory === 'all') return true;
    return citation.category === selectedCategory;
  });

  const itemsPerPage = 10;
  const totalItems = filteredResults?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredResults?.slice(startIndex, endIndex);

  // Reset to page 1 when data changes or when current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage, setCurrentPage, selectedCategory]);

  return (
    <Card className="yst-overflow-visible md:yst-col-span-2">
      <Card.Content className="yst-flex yst-flex-col yst-gap-6">
        <div className="yst-space-y-4">
          <TitleWithTooltip
            title="Citations"
            tooltip="Sources the AI used to support brand mentions in the results."
          />
          <p>
            This table highlights the most important sources where your brand
            was cited.
          </p>
          <TextScore
            score={citations?.total?.value}
            suffix="citations"
            delta={citations?.total?.previous?.difference}
            deltaSuffix="since last analysis"
            isLoading={isLoading}
            hideTotal
          />
        </div>

        {/* If citations list is empty */}
        {citations?.list?.length === 0 && !isLoading && !isRefetching ? (
          <Alert>
            The selected AI model returned no citations for your queries. This
            can happen with some queries or models.
          </Alert>
        ) : (
          <>
            {/* Filter & Table */}
            <div>
              {enableCategories && (
                <Select
                  className="yst-mb-4 md:yst-max-w-sm"
                  label="Filter categories"
                  value={selectedCategory}
                  onChange={(value: string) => {
                    setSelectedCategory(value);
                  }}
                  id="citation-category-filter"
                  // placeholder="All categories..."
                  options={[
                    { value: 'all', label: 'All categories' },
                    ...(citations?.categories
                      ? citations.categories.map((category) => ({
                          value: category,
                          label: category,
                        }))
                      : []),
                  ]}
                />
              )}
              <Table variant="minimal">
                <Table.Head className="yst-font-semibold">
                  <Table.Row>
                    <Table.Cell className="yst-hidden yst-text-slate-900 md:yst-table-cell md:yst-pl-2">
                      #
                    </Table.Cell>
                    <Table.Cell className="yst yst-hidden yst-text-slate-900 md:yst-table-cell md:yst-px-2">
                      URL
                    </Table.Cell>
                    <Table.Cell className="yst-hidden yst-text-slate-900 md:yst-table-cell md:yst-px-2">
                      Root domain
                    </Table.Cell>
                    {enableCategories && (
                      <Table.Cell className="yst-hidden yst-text-slate-900 md:yst-table-cell">
                        Category
                      </Table.Cell>
                    )}
                    <Table.Cell className="yst-hidden yst-text-slate-900 md:yst-table-cell md:yst-px-2">
                      Impact
                    </Table.Cell>
                    <Table.Cell className="yst-hidden yst-text-right yst-text-slate-900 md:yst-table-cell">
                      Mentions
                    </Table.Cell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {/* use loading */}
                  {isLoading || isRefetching ? (
                    // Show skeleton rows when loading
                    Array.from({ length: 5 }).map((_, index) => (
                      <CitationTableRow
                        key={`skeleton-${index}`}
                        isLoading
                        enableCategories={enableCategories}
                      />
                    ))
                  ) : currentPageItems && currentPageItems.length > 0 ? (
                    // Show filtered results
                    currentPageItems.map((citationData, index) => (
                      <CitationTableRow
                        key={`citation-table-row-${citationData.url}-${index}`}
                        data={{ ...citationData, rank: startIndex + index + 1 }}
                        totalQueries={scan?.numberOfQueries}
                        isFirstScan={!hasPreviousCitation}
                        enableCategories={enableCategories}
                      />
                    ))
                  ) : (
                    // Show "No results" message when filter returns empty
                    <Table.Row key="no-results">
                      <Table.Cell
                        colSpan={enableCategories ? 6 : 5}
                        className="yst-py-8 yst-text-center yst-text-slate-500"
                      >
                        {selectedCategory === 'all'
                          ? 'No citations found.'
                          : `No citations found for the "${selectedCategory}" category.`}{' '}
                        <Link onClick={() => setSelectedCategory('all')}>
                          Show all categories.
                        </Link>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>

            {filteredResults && filteredResults.length > itemsPerPage && (
              <div className="yst-flex yst-items-center yst-justify-end yst-gap-4">
                <p className="yst-mb-0">
                  Showing {startIndex + 1} to{' '}
                  {endIndex > totalItems ? totalItems : endIndex} of{' '}
                  {totalItems} results
                </p>
                <Pagination
                  variant="text"
                  className="yst-flex-row yst-gap-0"
                  current={currentPage}
                  total={totalPages}
                  onNavigate={setCurrentPage}
                  screenReaderTextPrevious="Previous page"
                  screenReaderTextNext="Next page"
                />
              </div>
            )}
          </>
        )}
      </Card.Content>
    </Card>
  );
};

export default CitationsCard;
